import { useMemo, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";

import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./WordImageMatch.css";

const WORDS_BANK = [
  {
    id: "mesa",
    word: "MESA",
    emoji: "🪑",
    imageLabel: "Una mesa",
    category: "Casa",
    difficulty: "easy",
    distractors: ["🛏️", "🚪", "🛋️", "🪟"],
  },
  {
    id: "luna",
    word: "LUNA",
    emoji: "🌙",
    imageLabel: "La luna",
    category: "Naturaleza",
    difficulty: "easy",
    distractors: ["☀️", "⭐", "☁️", "🌈"],
  },
  {
    id: "periodico",
    word: "PERIÓDICO",
    emoji: "📰",
    imageLabel: "Un periódico",
    category: "Objetos",
    difficulty: "easy",
    distractors: ["📕", "✉️", "📓", "📦"],
  },
  {
    id: "vaso",
    word: "VASO",
    emoji: "🥛",
    imageLabel: "Un vaso",
    category: "Cocina",
    difficulty: "easy",
    distractors: ["🍽️", "🥄", "🍴", "🥣"],
  },
  {
    id: "ventana",
    word: "VENTANA",
    emoji: "🪟",
    imageLabel: "Una ventana",
    category: "Casa",
    difficulty: "easy",
    distractors: ["🚪", "🪞", "🖼️", "🛏️"],
  },
  {
    id: "patata",
    word: "PATATA",
    emoji: "🥔",
    imageLabel: "Una patata",
    category: "Alimentos",
    difficulty: "easy",
    distractors: ["🍅", "🥕", "🍋", "🍎"],
  },
  {
    id: "cohete",
    word: "COHETE",
    emoji: "🚀",
    imageLabel: "Un cohete",
    category: "Transportes",
    difficulty: "easy",
    distractors: ["✈️", "🚁", "🛸", "🚌"],
  },
  {
    id: "cuadro",
    word: "CUADRO",
    emoji: "🖼️",
    imageLabel: "Un cuadro",
    category: "Objetos",
    difficulty: "easy",
    distractors: ["📺", "🪞", "📕", "🪟"],
  },
  {
    id: "flor",
    word: "FLOR",
    emoji: "🌸",
    imageLabel: "Una flor",
    category: "Naturaleza",
    difficulty: "easy",
    distractors: ["🌳", "🍃", "🌵", "🍄"],
  },
  {
    id: "fuego",
    word: "FUEGO",
    emoji: "🔥",
    imageLabel: "Fuego",
    category: "Naturaleza",
    difficulty: "easy",
    distractors: ["💧", "❄️", "☁️", "🌊"],
  },
  {
    id: "limon",
    word: "LIMÓN",
    emoji: "🍋",
    imageLabel: "Un limón",
    category: "Alimentos",
    difficulty: "easy",
    distractors: ["🍊", "🍎", "🍐", "🥝"],
  },
  {
    id: "tormenta",
    word: "TORMENTA",
    emoji: "⛈️",
    imageLabel: "Una tormenta",
    category: "Naturaleza",
    difficulty: "medium",
    distractors: ["☀️", "🌈", "🌤️", "🌙"],
  },
  {
    id: "transporte",
    word: "TRANSPORTE",
    emoji: "🚌",
    imageLabel: "Un medio de transporte",
    category: "Categorías",
    difficulty: "medium",
    distractors: ["🏠", "🌳", "🍎", "👕"],
  },
  {
    id: "alegria",
    word: "ALEGRÍA",
    emoji: "😄",
    imageLabel: "Una persona alegre",
    category: "Emociones",
    difficulty: "medium",
    distractors: ["😢", "😡", "😨", "😴"],
  },
  {
    id: "justicia",
    word: "JUSTICIA",
    emoji: "⚖️",
    imageLabel: "Una balanza que representa la justicia",
    category: "Conceptos abstractos",
    difficulty: "hard",
    distractors: ["💰", "👑", "🔒", "🎁"],
  },
  {
    id: "casa",
    word: "CASA",
    emoji: "🏠",
    imageLabel: "Una casa",
    category: "Lugares",
    difficulty: "easy",
    distractors: ["🏫", "🏥", "🏰", "⛪"],
  },
  {
    id: "perro",
    word: "PERRO",
    emoji: "🐕",
    imageLabel: "Un perro",
    category: "Animales",
    difficulty: "easy",
    distractors: ["🐈", "🐇", "🐎", "🐄"],
  },
  {
    id: "zapato",
    word: "ZAPATO",
    emoji: "👞",
    imageLabel: "Un zapato",
    category: "Ropa",
    difficulty: "easy",
    distractors: ["🧦", "👕", "🧤", "🎩"],
  },
  {
    id: "reloj",
    word: "RELOJ",
    emoji: "⌚",
    imageLabel: "Un reloj",
    category: "Objetos",
    difficulty: "easy",
    distractors: ["📱", "📻", "📷", "🧭"],
  },
  {
    id: "tijeras",
    word: "TIJERAS",
    emoji: "✂️",
    imageLabel: "Unas tijeras",
    category: "Material escolar",
    difficulty: "easy",
    distractors: ["✏️", "📏", "🖍️", "📎"],
  },
  {
    id: "hospital",
    word: "HOSPITAL",
    emoji: "🏥",
    imageLabel: "Un hospital",
    category: "Lugares",
    difficulty: "medium",
    distractors: ["🏫", "🏠", "🏦", "🏨"],
  },
  {
    id: "medico",
    word: "MÉDICO",
    emoji: "🧑‍⚕️",
    imageLabel: "Un médico",
    category: "Profesiones",
    difficulty: "medium",
    distractors: ["🧑‍🍳", "👮", "🧑‍🏫", "🧑‍🚒"],
  },
  {
    id: "bombero",
    word: "BOMBERO",
    emoji: "🧑‍🚒",
    imageLabel: "Un bombero",
    category: "Profesiones",
    difficulty: "medium",
    distractors: ["👮", "🧑‍⚕️", "🧑‍🍳", "🧑‍🏫"],
  },
  {
    id: "biblioteca",
    word: "BIBLIOTECA",
    emoji: "📚",
    imageLabel: "Libros de una biblioteca",
    category: "Lugares",
    difficulty: "medium",
    distractors: ["⚽", "🍽️", "🛒", "🎭"],
  },
  {
    id: "montaña",
    word: "MONTAÑA",
    emoji: "⛰️",
    imageLabel: "Una montaña",
    category: "Naturaleza",
    difficulty: "medium",
    distractors: ["🏝️", "🌊", "🏜️", "🌋"],
  },
  {
    id: "invierno",
    word: "INVIERNO",
    emoji: "❄️",
    imageLabel: "Nieve que representa el invierno",
    category: "Tiempo",
    difficulty: "medium",
    distractors: ["☀️", "🌸", "🍂", "🌈"],
  },
  {
    id: "cumpleaños",
    word: "CUMPLEAÑOS",
    emoji: "🎂",
    imageLabel: "Una tarta de cumpleaños",
    category: "Situaciones",
    difficulty: "medium",
    distractors: ["🎓", "💒", "🏖️", "🛏️"],
  },
  {
    id: "peligro",
    word: "PELIGRO",
    emoji: "⚠️",
    imageLabel: "Una señal de peligro",
    category: "Conceptos",
    difficulty: "medium",
    distractors: ["✅", "🎁", "💤", "🎵"],
  },
  {
    id: "silencio",
    word: "SILENCIO",
    emoji: "🤫",
    imageLabel: "Una persona pidiendo silencio",
    category: "Conceptos",
    difficulty: "medium",
    distractors: ["📣", "🎤", "😂", "👏"],
  },
  {
    id: "amistad",
    word: "AMISTAD",
    emoji: "🫂",
    imageLabel: "Dos personas abrazándose",
    category: "Conceptos abstractos",
    difficulty: "hard",
    distractors: ["🥊", "🚪", "💰", "😴"],
  },
  {
    id: "libertad",
    word: "LIBERTAD",
    emoji: "🕊️",
    imageLabel: "Una paloma que representa la libertad",
    category: "Conceptos abstractos",
    difficulty: "hard",
    distractors: ["🔒", "⛓️", "🚧", "🧱"],
  },
  {
    id: "paz",
    word: "PAZ",
    emoji: "☮️",
    imageLabel: "El símbolo de la paz",
    category: "Conceptos abstractos",
    difficulty: "hard",
    distractors: ["⚔️", "💣", "🔥", "🥊"],
  },
  {
    id: "igualdad",
    word: "IGUALDAD",
    emoji: "🟰",
    imageLabel: "El símbolo de igualdad",
    category: "Conceptos abstractos",
    difficulty: "hard",
    distractors: ["➕", "➖", "❌", "❓"],
  },
  {
    id: "generosidad",
    word: "GENEROSIDAD",
    emoji: "🎁",
    imageLabel: "Una persona ofreciendo un regalo",
    category: "Valores",
    difficulty: "hard",
    distractors: ["🔒", "💰", "🚫", "😡"],
  },
  {
    id: "cooperacion",
    word: "COOPERACIÓN",
    emoji: "🤝",
    imageLabel: "Dos personas colaborando",
    category: "Valores",
    difficulty: "hard",
    distractors: ["🥊", "🏃", "😴", "🙅"],
  },
  {
    id: "responsabilidad",
    word: "RESPONSABILIDAD",
    emoji: "✅",
    imageLabel: "Una tarea realizada correctamente",
    category: "Valores",
    difficulty: "hard",
    distractors: ["❌", "🎮", "💤", "🚫"],
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    allowedDifficulties: ["easy"],
    showCategory: true,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 4,
    allowedDifficulties: ["easy", "medium"],
    showCategory: true,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 5,
    allowedDifficulties: ["medium", "hard"],
    showCategory: false,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createRound = ({
  itemCount,
  allowedDifficulties,
  previousId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 2),
    5
  );

  const difficulties =
    Array.isArray(allowedDifficulties) &&
    allowedDifficulties.length > 0
      ? allowedDifficulties
      : ["easy"];

  const filteredWords = WORDS_BANK.filter(
    (item) =>
      item.id !== previousId &&
      difficulties.includes(item.difficulty)
  );

  const availableWords =
    filteredWords.length > 0
      ? filteredWords
      : WORDS_BANK.filter(
          (item) => item.id !== previousId
        );

  const selectedWord =
    availableWords[
      Math.floor(Math.random() * availableWords.length)
    ];

  const distractorCount = safeItemCount - 1;

  const selectedDistractors = shuffleArray(
    selectedWord.distractors
  ).slice(0, distractorCount);

  const options = shuffleArray([
    {
      id: `${selectedWord.id}-correct`,
      emoji: selectedWord.emoji,
      label: selectedWord.imageLabel,
      isCorrect: true,
    },

    ...selectedDistractors.map((emoji, index) => ({
      id: `${selectedWord.id}-distractor-${index}`,
      emoji,
      label: "Opción incorrecta",
      isCorrect: false,
    })),
  ]);

  return {
    ...selectedWord,
    options,
  };
};

function WordImageMatch() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);

  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [selectedOptionId, setSelectedOptionId] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const totalRounds = config?.totalRounds || 0;
  const totalAnswers = correctAnswers + errors;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const totalErrors = calculateErrorCount(errors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const defaultConfig =
      GAME_CONFIG[difficultyKey] ||
      GAME_CONFIG.easy;

    const nextConfig = {
      ...defaultConfig,
      ...resolvedConfig,
      difficultyKey,

      totalRounds:
        Number(resolvedConfig.totalRounds) ||
        defaultConfig.totalRounds,

      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) ||
            defaultConfig.itemCount,
          2
        ),
        5
      ),

      allowedDifficulties:
        resolvedConfig.allowedDifficulties ||
        defaultConfig.allowedDifficulties,

      showCategory:
        resolvedConfig.showCategory ??
        defaultConfig.showCategory,
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setSelectedOptionId(null);
    setResponseLocked(false);
  };

  const finishGame = () => {
    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousId: round.id,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);

    setSelectedOptionId(null);
    setResponseLocked(false);
    setRoundStartTime(Date.now());
  };

  const handleAnswer = (option) => {
    if (!round || responseLocked) {
      return;
    }

    setSelectedOptionId(option.id);
    setResponseLocked(true);

    const responseTime =
      Date.now() - roundStartTime;

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (option.isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );
    } else {
      setErrors((current) => current + 1);
    }

    window.setTimeout(() => {
      moveToNextRound();
    }, 750);
  };

  const getOptionClassName = (option) => {
    const isSelected =
      selectedOptionId === option.id;

    const classNames = [
      "word-image-option",
    ];

    if (
      responseLocked &&
      option.isCorrect
    ) {
      classNames.push(
        "word-image-option-correct"
      );
    }

    if (
      responseLocked &&
      isSelected &&
      !option.isCorrect
    ) {
      classNames.push(
        "word-image-option-error"
      );
    }

    return classNames.join(" ");
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Lectura de palabras"
        description="Lee la palabra y selecciona el dibujo que representa correctamente su significado."
        areas={[
          "Comprensión lectora",
          "Vocabulario",
          "Asociación semántica",
          "Acceso léxico",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Número de dibujos",
            min: 2,
            max: 5,
            defaultValue: 3,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="word-image-page">
        <div className="word-image-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalAnswers}
            accuracy={accuracy}
            errors={totalErrors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="word-image-results-grid">
            <div className="word-image-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="word-image-result-card">
              <span>Errores</span>
              <strong>{errors}</strong>
            </div>

            <div className="word-image-result-card">
              <span>Precisión</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>

            <div className="word-image-result-card">
              <span>Palabras realizadas</span>
              <strong>{totalAnswers}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="word-image-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="word-image-page">
      <main className="word-image-card">
        <header className="word-image-header">
          <div>
            <p className="word-image-area">
              Comprensión y lectura
            </p>

            <h1>Lectura de palabras</h1>

            <p>
              Lee la palabra y selecciona el dibujo
              que corresponde.
            </p>
          </div>

          <div className="word-image-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="word-image-game">
          {config?.showCategory && (
            <span className="word-image-category">
              {round?.category}
            </span>
          )}

          <div className="word-image-word-card">
            <span className="word-image-word-label">
              Palabra
            </span>

            <strong className="word-image-word">
              {round?.word}
            </strong>
          </div>

          <p className="word-image-instruction">
            Señala el dibujo que corresponde con la
            palabra.
          </p>

          <div
            className="word-image-options"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.options.length || 2,
                5
              )}, minmax(130px, 1fr))`,
            }}
          >
            {round?.options.map((option) => (
              <button
                type="button"
                key={option.id}
                className={getOptionClassName(
                  option
                )}
                onClick={() =>
                  handleAnswer(option)
                }
                disabled={responseLocked}
                aria-label={option.label}
                title={option.label}
              >
                <span
                  className="word-image-option-emoji"
                  aria-hidden="true"
                >
                  {option.emoji}
                </span>
              </button>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default WordImageMatch;